/**
 * このシーンだけ追加した素材 (overrides.added) の URL / 名前のキャッシュ。
 * docs/features/scene-frame-generation-tuning-plan.md §6.2。
 *
 * 候補関数は同期なので、生成・ドライランの前に ensure で埋めておく。
 * 取得失敗 (通信障害・見つからない) は無期限に残さず、FAILURE_TTL_MS を過ぎたら取り直す。
 * 一覧から外したときは forget で捨てる (再追加で取り直す)。
 */
export interface AddedMaterialInfo {
  url: string | null;
  name: string | null;
}

type Entry = AddedMaterialInfo & { failedAt: number | null };

export const ADDED_MATERIAL_FAILURE_TTL_MS = 30_000;

export class AddedMaterialCache {
  private entries = new Map<string, Entry>();
  private readonly fetcher: (id: string) => Promise<AddedMaterialInfo | null>;
  private readonly now: () => number;

  constructor(fetcher: (id: string) => Promise<AddedMaterialInfo | null>, now: () => number = () => Date.now()) {
    this.fetcher = fetcher;
    this.now = now;
  }

  get(id: string): AddedMaterialInfo | undefined {
    const e = this.entries.get(id);
    return e ? { url: e.url, name: e.name } : undefined;
  }

  forget(id: string): void {
    this.entries.delete(id);
  }

  /** 未取得・失敗して TTL を過ぎたものを取り直す */
  async ensure(ids: ReadonlyArray<string>): Promise<void> {
    const t = this.now();
    const targets = ids.filter((id) => {
      const e = this.entries.get(id);
      if (!e) return true;
      return e.failedAt != null && t - e.failedAt >= ADDED_MATERIAL_FAILURE_TTL_MS;
    });
    if (targets.length === 0) return;
    await Promise.all(targets.map(async (id) => {
      try {
        const info = await this.fetcher(id);
        if (info && info.url) {
          this.entries.set(id, { url: info.url, name: info.name, failedAt: null });
        } else {
          // 見つからない・画像でない: 当面は unresolved 扱い。TTL 後に取り直す (後から直る可能性がある)
          this.entries.set(id, { url: null, name: info?.name ?? null, failedAt: this.now() });
        }
      } catch {
        this.entries.set(id, { url: null, name: null, failedAt: this.now() });
      }
    }));
  }
}
